import React, { useState, useEffect } from 'react';
import { loginUser, registerUser, saveSearch, getFavorites, addFavorite, removeFavorite, recommendHybrid } from './utils/api';
import PreferenceForm from './components/PreferenceForm';
import ResultsDisplay from './components/ResultsDisplay';
import RadarChart from './components/RadarChart';
import SearchHistory from './components/SearchHistory';
import LoginForm from './components/LoginForm';
import RegisterForm from './components/RegisterForm';
import UserMenu from './components/UserMenu';
import FavoritesPanel from './components/FavoritesPanel';
import AccountPage from './components/AccountPage';
import AdminPage from './components/AdminPage';
import './App.css';

// The site is publicly accessible — no password gate. Login only unlocks
// personalized features (favorites, history, learned profile, admin).

function App() {
  const [currentUser, setCurrentUser] = useState(null);
  const [showLogin, setShowLogin] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [results, setResults] = useState([]);
  const [showRadarChart, setShowRadarChart] = useState(false);
  const [showSearchHistory, setShowSearchHistory] = useState(false);
  const [showFavorites, setShowFavorites] = useState(false);
  const [showAccount, setShowAccount] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [currentPreferences, setCurrentPreferences] = useState(null);
  const [currentPositions, setCurrentPositions] = useState(null);
  const [currentExclusions, setCurrentExclusions] = useState(null);
  const [favorites, setFavorites] = useState([]);

  useEffect(() => {
    // Check for saved user in localStorage
    const savedUser = localStorage.getItem('currentUser');
    if (savedUser) {
      try {
        setCurrentUser(JSON.parse(savedUser));
      } catch (error) {
        console.error('Error parsing saved user:', error);
      }
    }
  }, []);

  const handleLogin = async (username, password) => {
    try {
      const data = await loginUser(username, password);
      if (data.user) {
        setCurrentUser(data.user);
        localStorage.setItem('currentUser', JSON.stringify(data.user));
        setShowLogin(false);
        try {
          const favs = await getFavorites(data.user.username);
          setFavorites(favs || []);
        } catch (e) { /* ignore */ }
      }
    } catch (error) {
      throw error;
    }
  };

  const handleRegister = async (firstName, lastName, username, password) => {
    try {
      const data = await registerUser(firstName, lastName, username, password);
      if (data.user) {
        setCurrentUser(data.user);
        localStorage.setItem('currentUser', JSON.stringify(data.user));
        setShowRegister(false);
      }
    } catch (error) {
      throw error;
    }
  };

  const handleLogout = () => {
    setCurrentUser(null);
    localStorage.removeItem('currentUser');
    setResults([]);
    setShowRadarChart(false);
    setFavorites([]);
  };

  const handleResults = (foodResults, _mode) => {
    setResults(foodResults);
    setShowRadarChart(false);
    // Smooth scroll to results section
    setTimeout(() => {
      const el = document.getElementById('results-section');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const handleSearchSubmitted = async (preferences, positions, exclusions, searchResults) => {
    setCurrentPreferences(preferences);
    setCurrentPositions(positions);
    setCurrentExclusions(exclusions);
    
    if (currentUser) {
      try {
        await saveSearch(currentUser.username, preferences, positions, exclusions, searchResults);
      } catch (error) {
        console.error('Error saving search:', error);
      }
    }
  };

  const handleApplySearch = async (search) => {
    // Re-run a past search. recommend_hybrid logs store `sliders`;
    // save_search logs store `preferences` (which may embed hot_cold).
    try {
      const sliders = { ...(Object.keys(search.sliders || {}).length
        ? search.sliders
        : search.preferences || {}) };
      const hotCold = search.hot_cold || sliders.hot_cold || 'Both';
      delete sliders.hot_cold;

      const data = await recommendHybrid({
        username: currentUser?.username || '',
        sliders,
        seedFoods: search.seed_foods || [],
        context: { hot_cold: hotCold },
        exclusions: search.exclusions || {},
      });
      handleResults(data.results || [], data.mode);
    } catch (e) {
      console.error('Error re-running search:', e);
      alert('Could not re-run this search');
    }
  };

  const handleUserUpdated = (user) => {
    setCurrentUser(user);
    localStorage.setItem('currentUser', JSON.stringify(user));
  };

  const isAdmin = currentUser?.role === 'admin';

  return (
    <div className="App">
      {/* Authentication UI */}
      <div className="auth-container">
        {currentUser ? (
          <UserMenu
            currentUser={currentUser}
            onLogout={handleLogout}
            onShowSearchHistory={() => setShowSearchHistory(true)}
            onShowEditProfile={() => setShowAccount(true)}
            onShowFavorites={() => setShowFavorites(true)}
            onShowAdmin={() => setShowAdmin(true)}
          />
        ) : (
          <button className="login-btn" onClick={() => setShowLogin(true)}>
            Login
          </button>
        )}
      </div>

      {/* Login/Register Modals */}
      {showLogin && (
        <LoginForm
          onLogin={handleLogin}
          onClose={() => setShowLogin(false)}
          onSwitchToRegister={() => {
            setShowLogin(false);
            setShowRegister(true);
          }}
        />
      )}

      {showRegister && (
        <RegisterForm
          onRegister={handleRegister}
          onClose={() => setShowRegister(false)}
          onSwitchToLogin={() => {
            setShowRegister(false);
            setShowLogin(true);
          }}
        />
      )}

      {/* Search History */}
      {showSearchHistory && (
        <SearchHistory
          currentUser={currentUser}
          onApplySearch={handleApplySearch}
          isOpen={showSearchHistory}
          onClose={() => setShowSearchHistory(false)}
        />
      )}

      {showFavorites && (
        <FavoritesPanel
          isOpen={showFavorites}
          onClose={() => setShowFavorites(false)}
          favorites={favorites}
          onRemove={async (item) => {
            if (!currentUser) return;
            try {
              const res = await removeFavorite(currentUser.username, item);
              setFavorites(res.favorites || []);
            } catch (e) { /* ignore */ }
          }}
        />
      )}

      {/* Account settings */}
      {showAccount && currentUser && (
        <AccountPage
          currentUser={currentUser}
          onUserUpdated={handleUserUpdated}
          onClose={() => setShowAccount(false)}
        />
      )}

      {/* Admin console — menu entry is role-gated; actions re-verify server-side */}
      {showAdmin && isAdmin && (
        <AdminPage
          currentUser={currentUser}
          onClose={() => setShowAdmin(false)}
        />
      )}

      {/* Main Content — radar chart is admin-only (slated for rework) */}
      <PreferenceForm
        currentUser={currentUser}
        onResults={handleResults}
        onSearchSubmitted={handleSearchSubmitted}
        onShowRadarChart={isAdmin ? () => {
          if (results.length > 0 && currentPreferences) {
            setShowRadarChart(true);
          } else {
            alert('Please submit preferences first to see the radar chart');
          }
        } : undefined}
      />

      {results.length > 0 && (
        <>
          <div id="results-section">
          <ResultsDisplay
            results={results}
            onShowRadarChart={isAdmin ? () => setShowRadarChart(true) : undefined}
            currentUser={currentUser}
            favorites={favorites}
            onToggleFavorite={async (item, isFav) => {
              if (!currentUser) return;
              try {
                const res = isFav
                  ? await removeFavorite(currentUser.username, item)
                  : await addFavorite(currentUser.username, item);
                setFavorites(res.favorites || []);
              } catch (e) { /* ignore */ }
            }}
          />
          </div>
          
          {isAdmin && showRadarChart && currentPreferences && currentPositions && currentExclusions && (
            <RadarChart
              preferences={currentPreferences}
              positions={currentPositions}
              exclusions={currentExclusions}
              results={results}
              onClose={() => setShowRadarChart(false)}
            />
          )}
        </>
      )}
    </div>
  );
}

export default App;
